import { Box, Chip, Stack, Typography } from '@mui/material';

/**
 * `<GameMetadata>` — developer / publisher / genre chips rendered
 * near the top of the Game page.
 *
 * The content agent emits each of these as a first-class list on
 * the GameDetail record (round-26). Rendering the three facets as
 * chip rows keeps them scannable + searchable visually without
 * re-stating them in the summary prose.
 *
 * Sections render only when non-empty. When ALL three lists are
 * empty, the component returns `null`.
 *
 * R3.7 — every function ≤20 non-comment lines.
 */

interface GameMetadataProps {
  readonly developers: ReadonlyArray<string>;
  readonly publishers: ReadonlyArray<string>;
  readonly genres: ReadonlyArray<string>;
}

export function GameMetadata({ developers, publishers, genres }: GameMetadataProps): JSX.Element | null {
  const nothing = developers.length === 0 && publishers.length === 0 && genres.length === 0;
  if (nothing) return null;
  return (
    <Stack spacing={1}>
      {developers.length > 0 && <FacetRow label="Developer" values={developers} />}
      {publishers.length > 0 && <FacetRow label="Publisher" values={publishers} />}
      {genres.length > 0 && <FacetRow label="Genre" values={genres} />}
    </Stack>
  );
}

interface FacetRowProps {
  readonly label: string;
  readonly values: ReadonlyArray<string>;
}

/** One label + chip-strip row. */
function FacetRow({ label, values }: FacetRowProps): JSX.Element {
  const header = values.length > 1 ? `${label}s` : label;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: { sm: 90 }, fontWeight: 500 }}>
        {header}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {values.map((v) => <Chip key={v} label={v} size="small" variant="outlined" />)}
      </Box>
    </Stack>
  );
}
